/**
 * 角色设定类型定义
 *
 * 定义角色的身份、背景、性格等静态设定信息
 * 这些信息主要用于角色扮演和 AI Prompt 构建
 */

import { z } from 'zod';
import { normalizeRaceId } from '@/IsekaiRPG/modules/character/characterSemantic';

// ============================================
// 基础类型
// ============================================

/**
 * 性别
 */
export const GenderSchema = z.enum(['male', 'female', 'other']);
export type Gender = z.infer<typeof GenderSchema>;

// ============================================
// 塑造性经历
// ============================================

/**
 * 塑造性事件
 */
export const FormativeEventSchema = z.object({
  /** 关键事件 */
  event: z.string(),
  /** 该事件对性格/行为的影响 */
  impact: z.string(),
});
export type FormativeEvent = z.infer<typeof FormativeEventSchema>;

// ============================================
// 核心特质
// ============================================

/**
 * 性格特质
 */
export const PersonalityTraitSchema = z.object({
  /** 特质名称 */
  name: z.string(),
  /** 特质描述 */
  description: z.string(),
  /** 代表性台词 */
  dialogue: z.array(z.string()).default([]),
  /** 典型行为表现 */
  behavior: z.array(z.string()).default([]),
});
export type PersonalityTrait = z.infer<typeof PersonalityTraitSchema>;

/**
 * 核心特质
 */
export const CoreTraitsSchema = z.object({
  /** 性格特质列表 */
  traits: z.array(PersonalityTraitSchema),
});
export type CoreTraits = z.infer<typeof CoreTraitsSchema>;

/** 默认核心特质 */
export const DEFAULT_CORE_TRAITS: CoreTraits = {
  traits: [],
};

// ============================================
// 喜好与厌恶
// ============================================

/** 默认喜好列表 */
export const DEFAULT_LIKES: string[] = [];
/** 默认厌恶列表 */
export const DEFAULT_DISLIKES: string[] = [];

// ============================================
// 外貌
// ============================================

/**
 * 外貌描述
 */
export const AppearanceSchema = z.object({
  /** 身高 */
  height: z.string(),
  /** 体重 */
  weight: z.string(),
  /** 体型描述 */
  figure: z.string(),
  /** 三围（如需要） */
  measurements: z.string(),
  /** 头发描述 */
  hair: z.string(),
  /** 眼睛描述 */
  eyes: z.string(),
  /** 其他显著特征描述 */
  features: z.string(),
});
export type Appearance = z.infer<typeof AppearanceSchema>;

/** 默认外貌 */
export const DEFAULT_APPEARANCE: Appearance = {
  height: '',
  weight: '',
  figure: '',
  measurements: '',
  hair: '',
  eyes: '',
  features: '',
};

/**
 * 角色设定 (Profile)
 *
 * 包含角色的身份信息和 RP 相关描述
 * 这些信息在创建后基本不会改变
 */
export const CharacterProfileSchema = z.object({
  // ========== 基础信息 ==========
  /** 角色名称 */
  name: z.string(),
  /** 性别 */
  gender: GenderSchema,
  /** 年龄 */
  age: z.string().default(''),
  /** 头像（emoji 或图片 URL） */
  avatar: z.string().default(''),

  // ========== 身份信息 ==========
  /** 种族 */
  race: z.preprocess(value => normalizeRaceId(typeof value === 'string' ? value : undefined), z.string()).default(''),
  /** 国家/地区 */
  country: z.string().default(''),
  /** 语义标签（用于技能匹配、事件筛选等） */
  tags: z.array(z.string()).default([]),

  // ========== 身份描述 ==========
  /** 身份/头衔 */
  identity: z.string().default(''),
  /** 背景简述 */
  background: z.string().default(''),
  /** 战斗风格描述（生成阶段与行为推导共用） */
  combat_style: z.string().default(''),

  // ========== 塑造性经历 ==========
  /** 关键事件列表 */
  formativeEvents: z.array(FormativeEventSchema).default([]),

  // ========== 核心特质 ==========
  /** 核心特质与说话风格 */
  coreTraits: CoreTraitsSchema.default(DEFAULT_CORE_TRAITS),

  // ========== 喜好与厌恶 ==========
  /** 喜好 */
  likes: z.array(z.string()).default(DEFAULT_LIKES),
  /** 厌恶 */
  dislikes: z.array(z.string()).default(DEFAULT_DISLIKES),

  // ========== 外貌 ==========
  /** 外貌描述 */
  appearance: AppearanceSchema.default(DEFAULT_APPEARANCE),
});

export type CharacterProfile = z.infer<typeof CharacterProfileSchema>;

// ============================================
// 输入契约
// ============================================

/**
 * 角色 Stub：用于路人/任务发布者等轻量实体
 */
export const CharacterStubSchema = z.object({
  name: z.string().min(1),
  gender: GenderSchema.default('other'),
  race: z.preprocess(value => normalizeRaceId(typeof value === 'string' ? value : undefined), z.string()).default('人类'),
  identity: z.string().default(''),
  background: z.string().default(''),
  combat_style: z.string().optional(),
  tags: z.array(z.string()).default([]),
});
export type CharacterStub = z.infer<typeof CharacterStubSchema>;

/**
 * 角色档案完整输入：用于角色创建、种子数据等需要完整档案的场景
 */
export const CharacterProfileInputSchema = z.object({
  name: z.string().min(1),
  gender: GenderSchema.default('other'),
  race: z.preprocess(value => normalizeRaceId(typeof value === 'string' ? value : undefined), z.string()).default('人类'),
  age: z
    .union([z.string(), z.number()])
    .transform(value => String(value))
    .default(''),
  avatar: z.string().default(''),
  country: z.string().default(''),
  identity: z.string().default(''),
  background: z.string().default(''),
  combat_style: z.string().optional(),
  tags: z.array(z.string()).default([]),
  formativeEvents: z.array(FormativeEventSchema).default([]),
  coreTraits: CoreTraitsSchema.default(DEFAULT_CORE_TRAITS),
  likes: z.array(z.string()).default(DEFAULT_LIKES),
  dislikes: z.array(z.string()).default(DEFAULT_DISLIKES),
  appearance: AppearanceSchema.default(DEFAULT_APPEARANCE),
});
export type CharacterProfileInput = z.infer<typeof CharacterProfileInputSchema>;
